import { DataTransformationDto } from '@/interfaces/data-transformations'
import { IColumn, IRow } from '@/interfaces/interfaces'

const getBoundRowsnColumns = (
  rows: IRow[],
  columns: IColumn[],
  dataTransformation: DataTransformationDto
): [IRow[], IColumn[]] => {
  const transposed = dataTransformation?.transposed
  const boundColumns = columns.filter(({ index }) => dataTransformation?.columns.includes(index))
  const boundRows = rows.filter(({ index }) => dataTransformation?.rows.includes(index))
  return transposed ? [boundColumns, boundRows] : [boundRows, boundColumns]
}

type ReturnValues = [IRow[], IColumn[]]

/**
 * Get columns and rows of the data frame that is a result of data selections + data actions
 */
// An in-sequence net (issue #1130) is not in the table label list, so `rows[idx]`/`columns[idx]`
// miss it. Its name comes from the resulting frame's labels, which are aligned with
// resultingRows/resultingColumns by position; keep the net's id as the option value.
// `idx` is the frame id the backend expects, so always return it: some parsers (Generic -
// Wincross) emit label lists whose `index` drifts from the position (issue #2474).
const resolveMember = (
  source: (IRow | IColumn)[],
  idx: number,
  frameLabels: string[] | undefined,
  position: number
): IRow | IColumn => {
  const member = source[idx]
  if (member) return { name: member.name, index: idx }
  return { name: frameLabels?.[position], index: idx }
}

const getResultingRowsnColumns = (
  rows: IRow[],
  columns: IColumn[],
  dataTransformation: DataTransformationDto
): ReturnValues => {
  const frame = dataTransformation?.dataFrame
  let resultingRows = dataTransformation?.resultingRows.map((idx: number, i: number) =>
    resolveMember(dataTransformation.transposed ? columns : rows, idx, frame?.index, i)
  )
  let resultingColumns = dataTransformation?.resultingColumns.map((idx: number, i: number) =>
    resolveMember(dataTransformation.transposed ? rows : columns, idx, frame?.columns, i)
  )
  return [resultingRows, resultingColumns]
}

export { getBoundRowsnColumns, getResultingRowsnColumns }
export * from './tableUtils'
